/**
 * Collection Products Grid Section
 * Handles the initialization and management of collection product grid sections
 * including filters, accordions, and popup functionality.
 */

// Constants
const BREAKPOINTS = {
    TABLET: 768
};

/**
 * Section Manager class to handle collection grid section functionality
 */
class SectionManager {
    constructor() {
        this.filterForm = null;
        this.sectionContainer = null;
        this.sectionId = null;
        this.boundUpdateSection = this.updateSection.bind(this);
        this.boundAttachFormEvents = this.attachFormEvents.bind(this);
    }

    /**
     * Initialize the section manager
     * @returns {boolean} Whether initialization was successful
     */
    initialize() {
        this.filterForm = document.querySelector('.s-collection-filters__filters-popup .s-collection-filters__form');
        if (!this.filterForm) {
            console.error('Filter form not found');
            return false;
        }

        this.sectionId = this.filterForm.dataset.sectionId;
        this.sectionContainer = document.querySelector(`#s-collection-grid-${this.sectionId}`);
        
        if (!this.sectionContainer) {
            console.error('Section container not found');
            return false;
        }

        this.sectionContainer.classList.add('js-initialized');
        return true;
    }

    /**
     * Update the section content using the Section Rendering API
     */
    async updateSection() {
        try {
            const formData = new FormData(this.filterForm);
            const queryString = new URLSearchParams(formData).toString();
            const url = `${window.location.pathname}?${queryString}`;

            window.history.replaceState(null, null, url);

            const response = await fetch(`${url}&section_id=${this.sectionId}`, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch section: ${response.status}`);
            }

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const updatedSection = doc.querySelector(`#s-collection-grid-${this.sectionId}`);

            if (!updatedSection) {
                throw new Error('Updated section not found in the response HTML');
            }

            this.sectionContainer.innerHTML = updatedSection.innerHTML;
            this.initialize();
        } catch (error) {
            console.error('Error updating section:', error);
        }
    }

    /**
     * Attach form events based on viewport width
     */
    attachFormEvents() {
        if (window.innerWidth >= BREAKPOINTS.TABLET) {
            this.filterForm.addEventListener('change', this.boundUpdateSection);
        } else {
            this.filterForm.removeEventListener('change', this.boundUpdateSection);
        }
    }
}

/**
 * Accordion Manager class to handle accordion functionality
 */
class AccordionManager {
    constructor() {
        this.accordions = document.querySelectorAll('.accordion');
        this.collectionsAccordion = document.querySelector('.s-collection-filters__collections');
    }

    /**
     * Initialize accordion functionality
     */
    initialize() {
        this.accordions.forEach(accordion => {
            if (accordion === this.collectionsAccordion && window.innerWidth >= BREAKPOINTS.TABLET) {
                return;
            }

            const title = accordion.querySelector('.accordion__title');
            const content = accordion.querySelector('.accordion__content');

            if (!content || !title) return;

            const setMaxHeight = () => {
                accordion.classList.remove('accordion--open');
                content.style.maxHeight = '0';
            };

            setMaxHeight();

            title.addEventListener('click', () => {
                const isOpen = accordion.classList.contains('accordion--open');
                accordion.classList.toggle('accordion--open');
                content.style.maxHeight = isOpen ? '0' : `${content.scrollHeight}px`;
            });

            window.addEventListener('resize', setMaxHeight);
        });
    }
}

/**
 * Popup Manager class to handle filter popup functionality
 */
class PopupManager {
    constructor() {
        this.filterLabel = document.querySelector('.filter-label');
        this.popup = document.querySelector('.s-collection-filters__filters-popup');
        this.closeBtn = document.querySelector('.s-collection-filters__filters-popup--close');
    }

    /**
     * Initialize popup functionality
     */
    initialize() {
        if (!this.popup) return;

        this.popup.style.transform = 'translateX(-100%)';
        this.popup.style.transition = 'transform 0.3s ease-out';

        this.filterLabel?.addEventListener('click', () => {
            this.popup.style.transform = 'translateX(0)';
            document.documentElement.style.overflow = 'hidden';
        });

        this.closeBtn?.addEventListener('click', () => {
            this.popup.style.transform = 'translateX(-100%)';
            document.documentElement.style.overflow = '';
        });
    }
}

/**
 * Main initialization function
 */
const initializeCollectionGrid = () => {
    const sectionManager = new SectionManager();
    if (!sectionManager.initialize()) return;

    const accordionManager = new AccordionManager();
    const popupManager = new PopupManager();

    // Initialize all managers
    sectionManager.attachFormEvents();
    accordionManager.initialize();
    popupManager.initialize();

    // Handle window resize
    window.addEventListener('resize', sectionManager.boundAttachFormEvents);
};

// Initialize on page load
document.addEventListener('DOMContentLoaded', initializeCollectionGrid);